import { Link } from '../../../components/Link';
import { Seo } from '../../../lib/seo';
import { LegalLayout } from '../../../components/LegalLayout';
import { useLegal } from './useLegal';

/** Terms of Use — structure modelled on EmailListVerify's ToS, adapted for Maltese law. */
export function TermsPage() {
  const c = useLegal();
  return (
    <><Seo title="Terms of Use" description={`Terms governing your use of SieveGuard — account and eligibility rules, subscriptions and credits, acceptable use, liability limits, and how these terms change.`} path="/terms" /><LegalLayout title="Terms of Use" lastUpdated={c.lastUpdatedAt}>
      <p>
        These Terms of Use (&quot;<strong>Terms</strong>&quot;) form a binding agreement
        between you (&quot;<strong>Customer</strong>&quot;, &quot;<strong>you</strong>&quot;)
        and <strong>{c.legalEntityName}</strong>
        {!c.hideBusinessIdentity && (
          <>
            {' '}({c.companyType}, VAT {c.vatNumber}, registered at{' '}
            {c.registeredAddressLine})
          </>
        )}{' '}(&quot;<strong>SieveGuard</strong>&quot;, &quot;<strong>we</strong>&quot;).
        By creating an account or using the Service, you agree to these Terms.
      </p>

      <h2>1. Definitions</h2>
      <ul>
        <li>
          <strong>Service</strong>: the SieveGuard email-integrity API and associated console at{' '}
          <code>{c.websiteUrl}</code>.
        </li>
        <li>
          <strong>Plan</strong>: the published subscription tier you select.
        </li>
        <li>
          <strong>Subscription</strong>: your active billing arrangement with us.
        </li>
        <li>
          <strong>Trial</strong>: the platform-wide free-trial window applied to every new
          account.
        </li>
      </ul>

      <h2>2. Acceptance and changes</h2>
      <p>
        Continued use of the Service after we publish updated Terms constitutes acceptance.
        Material changes are notified to the email on file at least 30 days before they
        take effect.
      </p>

      <h2>3. Account registration</h2>
      <p>
        You must provide accurate information and keep your credentials secure. You are
        responsible for all activity under your account.
      </p>

      <h2>4. Subscriptions and billing</h2>
      <p>
        Subscriptions renew automatically at the end of each billing period (monthly or
        annual) until cancelled. Listed prices exclude VAT; Stripe Tax computes and adds
        any applicable VAT line at checkout based on your billing country and VAT-ID
        status (see §13). Annual plans are 10% off the monthly equivalent, and your{' '}
        <strong>first annual invoice</strong> receives a one-time welcome bonus equal to
        two months free (applied as a Stripe coupon on that invoice only — renewals
        charge the standard 10%-off annual rate). Payments are processed by Stripe;
        failed payments may suspend the Service until resolved.
      </p>
      <p>
        <strong>Add-on charges &amp; credit packs.</strong> In addition to your recurring
        Plan, you may purchase: (a) <em>recurring add-ons</em> — per-feature usage tiers
        billed as additional line items on your Plan invoice, pro-rated mid-cycle by
        Stripe; or (b) <em>one-time credit packs</em> — top-up balances of SieveGuard
        Credits charged immediately at checkout, non-recurring, with no cancellation or
        renewal. Purchased credits remain available until consumed and roll over month to
        month. Mid-cycle add-on upgrades may, at our discretion, be invoiced and
        collected immediately rather than accruing to your next regular invoice; you will
        always see the exact amount before confirming the change in the portal.
      </p>

      <h2>5. Free trial</h2>
      <p>
        New accounts may receive a free trial of the Service. No payment method is required
        during the trial. Access is automatically suspended at the end of the trial unless
        you subscribe to a Plan.
      </p>

      <h2>6. License grant</h2>
      <p>
        Subject to these Terms, we grant you a limited, revocable, non-exclusive,
        non-transferable license to use the Service solely for your own internal business
        purposes during your Subscription.
      </p>

      <h2>7. Restrictions</h2>
      <p>You may not:</p>
      <ul>
        <li>resell, sublicense, rent, lease, or distribute the Service to a third party;</li>
        <li>operate the Service as a timeshare, service-bureau, or white-label offering;</li>
        <li>reverse engineer, decompile or attempt to derive the source code;</li>
        <li>scrape, crawl, or otherwise access the Service except through documented APIs;</li>
        <li>circumvent quota, rate-limit, or trial enforcement;</li>
        <li>use the Service to build a competing product.</li>
      </ul>

      <h2>8. Acceptable use</h2>
      <p>
        Your use of the Service is bound by our <Link to="/aup">Acceptable Use Policy</Link>.
        Breach of the AUP is grounds for immediate suspension without refund.
      </p>

      <h2>9. Customer data</h2>
      <p>
        You retain ownership of email data you submit to the Service. We process it as a
        Processor on your behalf under the <Link to="/dpa">Data Processing Agreement</Link>.
      </p>

      <h2>10. Service availability</h2>
      <p>
        We use commercially reasonable efforts to maintain Service availability. Specific
        uptime guarantees apply only where expressly stated in a separately-signed SLA.
      </p>

      <h2>11. Suspension and termination</h2>
      <p>
        We may suspend or terminate your access for non-payment, AUP breach, or violation
        of these Terms. You may terminate at any time from the Subscription page; access
        runs until the end of your current billing period.
      </p>

      <h2>12. Refund policy</h2>
      <table>
        <thead>
          <tr>
            <th>Customer profile</th>
            <th>Refund right</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>EEA / UK consumer (B2C) — initial purchase</td>
            <td>14-day cooling-off period under the EU Consumer Rights Directive (pro-rata refund for usage)</td>
          </tr>
          <tr>
            <td>EU B2B with valid VAT ID</td>
            <td>No statutory cooling-off; cancellation effective end of billing period</td>
          </tr>
          <tr>
            <td>Annual plan cancelled within 30 days of auto-renewal</td>
            <td>Pro-rata refund for the unused portion</td>
          </tr>
          <tr>
            <td>Monthly plan</td>
            <td>Non-refundable after the cooling-off period; cancels at end of cycle</td>
          </tr>
          <tr>
            <td>Recurring add-on (per-feature tier)</td>
            <td>
              Cancellation effective end of the current billing period; Stripe credits the
              pro-rated unused portion to your next invoice on downgrade or removal
            </td>
          </tr>
          <tr>
            <td>One-time credit pack (B2B)</td>
            <td>
              Non-refundable once purchased; unconsumed credits remain in your wallet and
              roll over indefinitely
            </td>
          </tr>
          <tr>
            <td>One-time credit pack (EEA / UK consumer)</td>
            <td>
              14-day cooling-off applies; refund is reduced by the value of credits
              already consumed at the per-action cost in force at purchase
            </td>
          </tr>
        </tbody>
      </table>

      <h2>13. Fees and taxes</h2>
      <p>
        Listed prices exclude VAT. The applicable VAT line, where any, is computed by
        Stripe Tax at checkout from your billing country and VAT-ID status:
      </p>
      <ul>
        <li>
          <strong>EU business customers</strong> with a valid VAT ID in a Member State
          other than ours pay no VAT — the reverse-charge mechanism applies (Council
          Directive 2006/112/EC, Art. 196) and you self-account for the tax in your own
          return.
        </li>
        <li>
          <strong>Same-country business customers</strong> (resident in the same Member
          State as us, with a domestic VAT ID) are charged the standard local VAT rate;
          you may reclaim it via your local VAT return.
        </li>
        <li>
          <strong>EU consumers</strong> (no VAT ID) are charged VAT at the rate of their
          country of residence under the Union One-Stop-Shop (OSS) scheme.
        </li>
        <li>
          <strong>UK customers</strong> are charged UK VAT in accordance with
          post-Brexit UK rules; UK businesses providing a valid UK VAT number are subject
          to reverse charge under HMRC guidance.
        </li>
        <li>
          <strong>Non-EU/UK customers</strong> are not charged VAT — the supply is treated
          as an export of services (Council Directive 2006/112/EC, Art. 44/59) and is
          outside the scope of EU VAT.
        </li>
      </ul>
      <p>
        You are responsible for any other taxes due in your jurisdiction (income tax,
        sales tax, GST, withholding tax, etc.) and for keeping your billing country and
        VAT ID under <em>Settings → Billing</em> up to date so that VAT is computed
        correctly on each renewal.
      </p>

      <h2>14. Confidentiality</h2>
      <p>
        Each party agrees to protect the other&apos;s confidential information with at least
        the same degree of care used to protect its own, and no less than reasonable care.
      </p>

      <h2>15. Intellectual property</h2>
      <p>
        We retain all rights, title, and interest in the Service, including all software,
        documentation, and trademarks. You retain rights to your submitted data.
      </p>

      <h2>16. Warranties and disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot;. TO THE
        MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED,
        INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
      </p>

      <h2>17. Limitation of liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY ARISING OUT OF OR
        RELATING TO THESE TERMS WILL NOT EXCEED THE FEES YOU PAID TO US IN THE 12 MONTHS
        PRECEDING THE EVENT GIVING RISE TO THE CLAIM. WE WILL NOT BE LIABLE FOR INDIRECT,
        INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.
      </p>

      <h2>18. Indemnification</h2>
      <p>
        You will indemnify us against third-party claims arising from your breach of these
        Terms, your AUP violations, or unlawful content you submit through the Service.
      </p>

      <h2>19. Governing law and jurisdiction</h2>
      <p>
        {c.hideBusinessIdentity ? (
          <>
            These Terms are governed by the laws of the jurisdiction where SieveGuard is
            established, without regard to conflict-of-laws principles. The courts of that
            jurisdiction have exclusive competence over disputes, except that either party
            may seek injunctive relief in any competent court. The applicable jurisdiction
            will be identified on request to <code>{c.supportEmail}</code>.
          </>
        ) : (
          <>
            These Terms are governed by the laws of the <strong>{c.governingLawJurisdiction}</strong>,
            without regard to conflict-of-laws principles. The courts of {c.governingLawJurisdiction}{' '}
            have exclusive jurisdiction over disputes, except that either party may seek injunctive
            relief in any competent court.
          </>
        )}
      </p>

      <h2>20. Miscellaneous</h2>
      <p>
        These Terms, together with the Privacy Policy, DPA and AUP, are the entire agreement
        between us. If any provision is held unenforceable, the rest remains in effect. You
        may not assign these Terms without our written consent; we may assign them in
        connection with a merger or sale of substantially all our assets. Notices to us must
        be sent to <code>{c.supportEmail}</code>.
      </p>
    </LegalLayout></>
  );
}

// Default export required by React Router framework-mode route modules.
export default TermsPage;
